import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { movieBookingApi } from '../../services/movieBookingApi';
import { getMovieBookingRole, isAdminRole } from './movieBookingRole';
import './MovieBooking.css';

const AdminShowForm = () => {
  const { user } = useAuth();
  const isAdmin = isAdminRole(user) || getMovieBookingRole() === 'ADMIN';
  const [searchParams] = useSearchParams();
  const [theatres, setTheatres] = useState([]);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState({
    movieId: '',
    theatreId: '',
    screenId: 'SCREEN-1',
    city: '',
    showDate: new Date().toISOString().slice(0, 10),
    startTime: '18:00',
    endTime: '20:30',
    basePrice: 200,
    seatIds: 'A1,A2,A3,A4'
  });
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const loadLookups = async () => {
      try {
        const [theatreResponse, movieResponse] = await Promise.all([
          movieBookingApi.getAdminTheatres(),
          movieBookingApi.getMovies(),
        ]);
        const theatresList = theatreResponse.data || [];
        const moviesList = movieResponse.data || [];
        setTheatres(theatresList);
        setMovies(moviesList);

        const preselectedTheatreId = searchParams.get('theatreId');
        const defaultTheatreId = preselectedTheatreId || theatresList[0]?.id || '';
        const defaultMovieId = moviesList[0]?.id || '';

        setForm((prev) => ({
          ...prev,
          theatreId: defaultTheatreId,
          movieId: prev.movieId || defaultMovieId,
          city: prev.city || theatresList.find((t) => t.id === defaultTheatreId)?.city || prev.city,
        }));
      } catch (error) {
        setTheatres([]);
        setMovies([]);
      } finally {
        setLoading(false);
      }
    };
    if (isAdmin) {
      setLoading(true);
      loadLookups();
    }
  }, [isAdmin, searchParams, reloadKey]);

  const handleChange = (field, value) => {
    if (field === 'theatreId') {
      const selected = theatres.find((theatre) => theatre.id === value);
      setForm((prev) => ({
        ...prev,
        theatreId: value,
        city: selected?.city || '',
      }));
      return;
    }
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (!isAdmin) {
    return (
      <div className="movie-booking-page">
        <h1 className="movie-booking-title">Admin access required</h1>
        <p className="movie-booking-note">Switch to Admin role to manage shows.</p>
      </div>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    try {
      const payload = {
        ...form,
        basePrice: Number(form.basePrice),
        seatIds: form.seatIds.split(',').map((seat) => seat.trim()).filter(Boolean),
      };
      const response = await movieBookingApi.createShow(payload);
      setMessage(`Show created: ${response.data?.id}`);
    } catch (error) {
      setMessage('Failed to create show.');
    }
  };

  return (
    <div className="movie-booking-page">
      <div className="movie-booking-header">
        <h1 className="movie-booking-title">Create Show</h1>
        <div>
          <button className="movie-booking-button secondary" onClick={() => setReloadKey((value) => value + 1)}>
            Refresh
          </button>
          <button className="movie-booking-button secondary" onClick={() => navigate('/movies/admin')}>
            Back
          </button>
        </div>
      </div>

      {loading && <p>Loading theatres and movies...</p>}

      <form className="movie-booking-form" onSubmit={handleSubmit}>
        <p className="movie-booking-note">
          Select a movie and theatre, then set show time, price, and seats.
        </p>
        {movies.length ? (
          <>
            <label htmlFor="movie-id" className="movie-booking-note">Movie</label>
            <select
              id="movie-id"
              className="movie-booking-input"
              value={form.movieId}
              onChange={(event) => handleChange('movieId', event.target.value)}
              required
            >
              <option value="" disabled>Select movie</option>
              {movies.map((movie) => (
                <option key={movie.id} value={movie.id}>
                  {movie.title || movie.id}
                </option>
              ))}
            </select>
          </>
        ) : (
          <>
            <label htmlFor="movie-id" className="movie-booking-note">Movie ID</label>
            <input
              id="movie-id"
              className="movie-booking-input"
              placeholder="Movie ID (no movies found)"
              value={form.movieId}
              onChange={(event) => handleChange('movieId', event.target.value)}
              required
            />
            <p className="movie-booking-note">
              No movies available. Start the movie catalog service and add movies.
            </p>
          </>
        )}
        <label htmlFor="theatre-id" className="movie-booking-note">Theatre</label>
        <select
          id="theatre-id"
          className="movie-booking-input"
          value={form.theatreId}
          onChange={(event) => handleChange('theatreId', event.target.value)}
          required
        >
          <option value="" disabled>Select theatre</option>
          {theatres.map((theatre) => (
            <option key={theatre.id} value={theatre.id}>
              {theatre.name} ({theatre.city})
            </option>
          ))}
        </select>
        <label htmlFor="screen-id" className="movie-booking-note">Screen</label>
        <input
          id="screen-id"
          className="movie-booking-input"
          placeholder="Screen ID (e.g. SCREEN-1)"
          value={form.screenId}
          onChange={(event) => handleChange('screenId', event.target.value)}
          required
        />
        <label htmlFor="city" className="movie-booking-note">City</label>
        <input
          id="city"
          className="movie-booking-input"
          placeholder="City"
          value={form.city}
          onChange={(event) => handleChange('city', event.target.value)}
          required
        />
        <label htmlFor="show-date" className="movie-booking-note">Show Date</label>
        <input
          id="show-date"
          className="movie-booking-input"
          type="date"
          value={form.showDate}
          onChange={(event) => handleChange('showDate', event.target.value)}
          required
        />
        <label htmlFor="start-time" className="movie-booking-note">Start Time</label>
        <input
          id="start-time"
          className="movie-booking-input"
          type="time"
          value={form.startTime}
          onChange={(event) => handleChange('startTime', event.target.value)}
          required
        />
        <label htmlFor="end-time" className="movie-booking-note">End Time</label>
        <input
          id="end-time"
          className="movie-booking-input"
          type="time"
          value={form.endTime}
          onChange={(event) => handleChange('endTime', event.target.value)}
          required
        />
        <label htmlFor="base-price" className="movie-booking-note">Base Price</label>
        <input
          id="base-price"
          className="movie-booking-input"
          type="number"
          min="0"
          value={form.basePrice}
          onChange={(event) => handleChange('basePrice', event.target.value)}
          required
        />
        <label htmlFor="seat-ids" className="movie-booking-note">Seat IDs</label>
        <input
          id="seat-ids"
          className="movie-booking-input"
          placeholder="Comma-separated (e.g. A1,A2,A3)"
          value={form.seatIds}
          onChange={(event) => handleChange('seatIds', event.target.value)}
        />
        <p className="movie-booking-note">
          Seats are optional. If omitted, seats will not be pre-initialized.
        </p>
        <button className="movie-booking-button" type="submit">Create Show</button>
      </form>
      {message && <p className="movie-booking-note">{message}</p>}
    </div>
  );
};

export default AdminShowForm;
